// The sequence clock.
//
// One requestAnimationFrame loop lives in the host; every renderer subscribes to
// it and paints its own DOM. That split is deliberate:
//
//   - The host owns anything that must happen exactly once per run — the route
//     commit, phase publication, the watchdog, teardown guarantees.
//   - Each renderer owns its own elements and its own interpolation, so adding
//     a sequence never means editing shared orchestration.
//
// Renderers write styles directly rather than going through an animation
// library. That is not a stylistic preference: declarative framer-motion
// keyframe tracks were tried first for the tape transport and silently never
// started inside the portal (see the header comment in TapeTransportRenderer).
// A sequence that is a pure function of elapsed time is also reproducible,
// frame-rate independent, and scrubable — which is how these get tuned.

import { createContext, useContext, useEffect, useRef } from 'react';

export type FrameFn = (elapsed: number) => void;

export interface SequenceClock {
  /** Returns an unsubscribe function. */
  subscribe: (fn: FrameFn) => () => void;
  /** Elapsed seconds at the most recent tick — for renderers mounting late. */
  now: () => number;
}

export const ClockContext = createContext<SequenceClock | null>(null);

/**
 * Create a clock plus the driver used to advance it. The host holds both; the
 * clock alone goes into context.
 */
export function createSequenceClock(): {
  clock: SequenceClock;
  emit: (elapsed: number) => void;
} {
  const subscribers = new Set<FrameFn>();
  let last = 0;

  return {
    clock: {
      subscribe(fn) {
        subscribers.add(fn);
        return () => subscribers.delete(fn);
      },
      now: () => last,
    },
    emit(elapsed) {
      last = elapsed;
      // Copied before iteration: a renderer unsubscribing mid-frame (it can,
      // if a paint triggers an unmount) must not perturb this pass.
      for (const fn of [...subscribers]) fn(elapsed);
    },
  };
}

/**
 * Subscribe to the running sequence's clock for the lifetime of the component.
 * The callback is held in a ref so a renderer can close over fresh props
 * without resubscribing every render.
 */
export function useSequenceFrame(fn: FrameFn): void {
  const clock = useContext(ClockContext);
  const held = useRef(fn);
  held.current = fn;

  useEffect(() => {
    if (!clock) return;
    const unsub = clock.subscribe((elapsed) => held.current(elapsed));
    // Paint immediately at the current time so a renderer that mounts partway
    // through a run doesn't show one unstyled frame.
    held.current(clock.now());
    return unsub;
  }, [clock]);
}
